import axios from 'axios';
import { TOKEN_KEY } from './auth';

// Set proper default values for API endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRODUCT_PURCHASES_ENDPOINT = '/product-purchases';

// Create axios instance with default baseURL
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000 // 10 second timeout
});

// Add request interceptor to attach the token to all requests
api.interceptors.request.use(config => {
  console.log('API Request:', config.method, config.url);
  
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    // Set the Authorization header with 'Bearer ' prefix
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Token attached to request');
  } else {
    console.log('No token found in localStorage');
  }
  
  return config;
}, error => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor for debugging
api.interceptors.response.use(response => {
  console.log('API Response:', response.status, response.config.url);
  return response;
}, error => {
  console.error('API Error:', error.response?.status, error.response?.data, error.config?.url);
  return Promise.reject(error);
});

/**
 * Get all purchases for a specific product
 * @param {number} productId - The ID of the product
 * @returns {Promise<Array>} Array of purchase records
 */
export const getProductPurchases = async (productId) => {
  try {
    const response = await api.get(`${PRODUCT_PURCHASES_ENDPOINT}/product/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product purchases:', error);
    throw error;
  }
};

/**
 * Add a new product purchase
 * @param {Object} purchaseData - The purchase data
 * @returns {Promise<Object>} The created purchase
 */
export const addProductPurchase = async (purchaseData) => {
  try {
    // Check if user is authenticated before making request
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }
    
    const response = await api.post(PRODUCT_PURCHASES_ENDPOINT, purchaseData);
    return response.data;
  } catch (error) {
    console.error('Error adding product purchase:', error);
    throw error;
  }
};

/**
 * Get inventory valuation report
 * @param {string} method - Valuation method: 'fifo', 'lifo', or 'average'
 * @returns {Promise<Object>} Inventory valuation report
 */
export const getInventoryValuation = async (method = 'average') => {
  try {
    const response = await api.get(`${PRODUCT_PURCHASES_ENDPOINT}/valuation?method=${method}`);
    return response.data;
  } catch (error) {
    console.error('Error getting inventory valuation:', error);
    throw error;
  }
}; 